import { useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useReactTable, getCoreRowModel, flexRender, getSortedRowModel, getFilteredRowModel, getPaginationRowModel } from "@tanstack/react-table";

/**
 * Универсальная таблица с виртуализацией и подсветкой ячеек по нормам.
 * @param {Object} props - Параметры компонента.
 * @param {Array} props.data - Данные для отображения.
 * @param {Array} props.columns - Определение колонок.
 * @param {Object} props.styles - Дополнительные стили для обертки.
 * @param {string} props.noDataText - Текст для отображения при отсутствии данных.
 * @param {Function} props.getCellStyle - Функция для определения стилей ячейки. Принимает (value, columnId, row) и возвращает объект стилей.
 * @param {number} props.frozenColumnCount - Количество столбцов, которые нужно закрепить при горизонтальной прокрутке (по умолчанию 0).
 * @param {string} props.tableName - Название таблицы, которое будет использоваться в имени файла при экспорте (по умолчанию 'таблица').
 * @param {Function} [props.onCellClick] - Функция обратного вызова, вызываемая при клике на ячейку. Принимает (value, columnId, row, cellData, event).
 * @param {Object} [props.cellData] - Объект с дополнительными данными для ячеек, где ключ - id колонки, значение - объект с данными для каждой строки (например, { columnId: { rowId: 'доп. данные' } }).
 * @param {number} [props.pageSize] - Количество строк на странице (по умолчанию 5).
 */
const VirtualizedTable = ({
  data = [],
  columns = [],
  styles = {},
  noDataText = 'Нет данных',
  getCellStyle = () => ({}),
  frozenColumnCount = 0,
  tableName = 'таблица',
  onCellClick = null,
  cellData = {},
  pageSize = 5,
}) => {
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize });
  const [isExporting, setIsExporting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Ссылка на контейнер меню
  const menuRef = useRef();

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      pagination,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetAll: false,
  });

  // Эффект для закрытия меню при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuRef]);

  return (
    <div style={{ width: '100%', ...styles.wrapper }}>
      {/* Панель инструментов */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div>
          <input
            value={globalFilter ?? ''}
            onChange={e => setGlobalFilter(e.target.value)}
            placeholder="Поиск..."
            style={{
              padding: '6px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              width: '200px'
            }}
          />
        </div>
          <div style={{ position: 'relative', display: 'inline-block' }} ref={menuRef}>
            {/* Кнопка с тремя точками */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              style={{
                padding: '6px 12px',
                background: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              ⋮
            </button>
            
            {/* Выпадающее меню */}
            {menuOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                background: 'white',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                zIndex: 10,
                minWidth: '150px'
              }}>
              <div style={{ padding: '8px' }}>
                <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>Показать:</div>
                {[5, 10, 25].map(pageSize => (
                  <button
                    key={pageSize}
                    onClick={() => {
                      table.setPageSize(pageSize);
                      setMenuOpen(false);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '4px 8px',
                      textAlign: 'left',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    {pageSize}
                  </button>
                ))}
              </div>
              <hr style={{ margin: 0, borderColor: '#eee' }} />
              <button
                onClick={async () => {
                  setIsExporting(true);
                  try {
                    // Создаем рабочую книгу
                    const wb = XLSX.utils.book_new();
                    
                    // Создаем массив для данных
                    const worksheetData = [];
                    
                    // Добавляем заголовки
                    const headerRow = columns.map(col => col.header);
                    worksheetData.push(headerRow);
                    
                    // Добавляем строки данных
                    data.forEach(row => {
                      const dataRow = columns.map(col => {
                        // Используем accessorKey или id для получения значения
                        const key = col.accessorKey || col.id;
                        return row[key];
                      });
                      worksheetData.push(dataRow);
                    });
                    
                    // Создаем лист
                    const ws = XLSX.utils.aoa_to_sheet(worksheetData);
                    
                    // Добавляем лист в книгу
                    XLSX.utils.book_append_sheet(wb, ws, 'Данные');
                    
                    // Генерируем и скачиваем файл
                    XLSX.writeFile(wb, `${tableName}.xlsx`);
                  } catch (error) {
                    console.error('Ошибка при экспорте в Excel:', error);
                  } finally {
                    setIsExporting(false);
                    setMenuOpen(false);
                  }
                }}
                disabled={isExporting || data.length === 0}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  cursor: isExporting || data.length === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                {isExporting ? 'Экспорт...' : 'Скачать Excel'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Таблица */}
      <div style={{border: '1px solid #e0e0e0', borderRadius: '4px', position: 'relative' }}>
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{
                      width: `${header.getSize()}px`,
                      background: '#f5f5f5',
                      border: '1px solid #ddd',
                      padding: '8px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      ...(header.index < frozenColumnCount ? { left: header.getStart(), zIndex: 2 } : {})
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {{
                      asc: ' 🔼',
                      desc: ' 🔽',
                    }[header.column.getIsSorted()] ?? null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      style={{
                        width: `${cell.column.getSize()}px`,
                        border: '1px solid #eee',
                        padding: '8px',
                        ...getCellStyle(cell.getValue(), cell.column.id, row.original),
                        ...(cell.column.getIsVisible() && cell.column.index < frozenColumnCount ? { position: 'sticky', left: `${cell.column.getStart()}px`, background: 'white', zIndex: 1 } : {}),
                        cursor: onCellClick ? 'pointer' : 'default'
                      }}
                      onClick={(e) => {
                        if (onCellClick) {
                          // Получаем дополнительные данные для этой ячейки
                          const extraData = cellData[cell.column.id]?.[row.id] || null;
                          onCellClick(cell.getValue(), cell.column.id, row.original, extraData, e);
                        }
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>
                  {noDataText}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Пагинация */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
        <button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          style={{
            padding: '6px 12px',
            margin: '0 4px',
            background: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: table.getCanPreviousPage() ? 'pointer' : 'not-allowed'
          }}
        >
          Назад
        </button>
        <span>
          Страница{' '}
          <strong>
            {table.getState().pagination.pageIndex + 1} из {table.getPageCount()}
          </strong>
        </span>
        <button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          style={{
            padding: '6px 12px',
            margin: '0 4px',
            background: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: table.getCanNextPage() ? 'pointer' : 'not-allowed'
          }}
        >
          Вперед
        </button>
      </div>
    </div>
  );
};

export default VirtualizedTable;